<template>
  <div class="ap-card h-100 p-0 overflow-hidden">
    <div class="card-header-styled">
      <i class="fas fa-calendar-alt me-1"></i>Select Date &amp; Time
    </div>
    <div class="p-3 pt-2">
      <div class="week-nav">
        <button type="button" class="nav-btn" @click="$emit('change-week', -1)">
          <i class="fas fa-chevron-left"></i>
        </button>
        <div class="days-strip">
          <button
            type="button"
            v-for="(day, i) in weekDays"
            :key="i"
            class="day-btn"
            :class="{ active: isSelectedDate(day.date), 'no-slot': !day.hasSlot }"
            :title="day.hasSlot ? day.slots.length + ' slot(s)' : 'No slots'"
            @click="$emit('select-date', day)"
          >
            <span class="dn">{{ day.dayName }}</span>
            <span class="dd">{{ day.date.getDate() }}</span>
            <span class="dm">{{ day.monthName }}</span>
            <span class="dot"></span>
          </button>
        </div>
        <button type="button" class="nav-btn" @click="$emit('change-week', 1)">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>

      <div class="mt-2">
        <div v-if="!selectedDay" class="hint-msg">
          <i class="fas fa-mouse-pointer me-1"></i>Pick a date above to see available slots.
        </div>
        <div v-else-if="selectedDay.slots.length === 0" class="no-slot-msg">
          <i class="fas fa-times-circle me-1"></i>No slots on this day — try another date.
        </div>
        <div v-else class="slots-wrap">
          <button
            type="button"
            v-for="slot in selectedDay.slots"
            :key="slot"
            class="slot-chip"
            :class="{ selected: selectedSlot === slot }"
            @click="$emit('select-slot', slot)"
          >
            <i class="fas fa-clock me-1"></i>{{ slot }}
          </button>
        </div>
      </div>

      <span v-if="errors.slot" class="err-msg mb-2">{{ errors.slot }}</span>

      <div class="confirm-bar" v-if="selectedDay && selectedSlot">
        <i class="fas fa-check-circle me-2"></i>
        <span>
          <strong>{{ formattedSelectedDate }}</strong>
          &nbsp;·&nbsp;
          <strong>{{ selectedSlot }}</strong>
        </span>
        <span class="ms-auto confirm-fee">Fee: <strong>৳ {{ doctor.sales_price }}</strong></span>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  weekDays: {
    type: Array,
    required: true,
  },
  selectedDay: {
    type: Object,
    default: null,
  },
  selectedSlot: {
    type: String,
    default: null,
  },
  formattedSelectedDate: {
    type: String,
    default: '',
  },
  doctor: {
    type: Object,
    required: true,
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
});

defineEmits(['change-week', 'select-date', 'select-slot']);

const isSelectedDate = (date) => {
  return props.selectedDay && date.toDateString() === props.selectedDay.date.toDateString();
};
</script>

<style scoped>
.ap-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 16px rgba(115, 103, 240, 0.1);
  padding: 18px 20px;
}

.card-header-styled {
  background: #f8f8f8;
  padding: 12px 20px;
  border-bottom: 1px solid #e4e1f0;
  font-size: 1rem;
  font-weight: 700;
  color: #2d2d3a;
  display: flex;
  align-items: center;
}
.card-header-styled i {
  color: #7367f0;
  font-size: 1.1rem;
}

.week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.nav-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1.5px solid #7367f0;
  background: transparent;
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;
}
.nav-btn:hover {
  background: #7367f0;
  color: #fff;
}

.days-strip {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.day-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1.5px solid #e4e1f0;
  background: #fff;
  border-radius: 9px;
  padding: 6px 2px;
  cursor: pointer;
  transition: all 0.18s;
  gap: 1px;
}
.day-btn:hover {
  border-color: #9186f3;
  background: #ede9fd;
}
.day-btn.active {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
}
.day-btn.no-slot {
  opacity: 0.5;
}

.dn {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  opacity: 0.65;
}
.dd {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
}
.dm {
  font-size: 9px;
  opacity: 0.55;
}
.day-btn.active .dn,
.day-btn.active .dm {
  color: rgba(255, 255, 255, 0.8);
  opacity: 1;
}

.dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #0bc5a4;
  margin-top: 2px;
}
.day-btn.no-slot .dot {
  background: #ddd;
}
.day-btn.active .dot {
  background: rgba(255, 255, 255, 0.55);
}

.slots-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.slot-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1.5px solid #e4e1f0;
  background: #fff;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s;
}
.slot-chip:hover {
  border-color: #7367f0;
  background: #ede9fd;
  color: #7367f0;
}
.slot-chip.selected {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
}

.hint-msg {
  font-size: 0.82rem;
  color: #6e6b7b;
}
.no-slot-msg {
  font-size: 0.82rem;
  color: #e53e3e;
}

.confirm-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 9px 14px;
  background: #ede9fd;
  border: 1px solid rgba(115, 103, 240, 0.2);
  border-radius: 6px;
  font-size: 0.82rem;
}
.confirm-bar i {
  color: #7367f0;
}
.confirm-bar strong {
  color: #7367f0;
}
.confirm-fee {
  font-size: 0.78rem;
  color: #6e6b7b;
  white-space: nowrap;
}
.confirm-fee strong {
  color: #0a9e85;
}

.err-msg {
  display: block;
  font-size: 0.75rem;
  color: #ea5455;
  margin-top: 4px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .days-strip {
    grid-template-columns: repeat(4, 1fr);
  }
}
@media (max-width: 576px) {
  .days-strip {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
